import { readOnly } from '@ember/object/computed';
import { later } from '@ember/runloop';
import { task } from 'ember-concurrency';
import { Promise } from 'rsvp';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import {
  POSTING__ASSIGNMENT_BUYER_INCLUDES,
  POSTING__ASSIGNMENT_SELLER_INCLUDES,
  POSTING_APPLICATIONS_PER_PAGE,
  POSTING_APPLICATIONS_SORT_API_ENUMS
} from "../../../constants/routes/posting";
import { v1 } from "ember-uuid";
import { APPLICATION } from "../../../models/application";
import { ICON_SORTS } from "../../../constants/lsg";
import ShowDeclineOtherApplicationsModalMixin
  from "../../../mixins/applications/show-decline-other-applications-modal-mixin";
import { ASSIGNMENT } from "../../../models/assignment";

/**
 * The number of requests for that will allow in flight at any given time while we're preloading
 * all the pages.
 *
 * @type {number}
 */
const NUMBER_OF_PAGES_TO_LOAD_IN_PARALLEL = 3;

/**
 * The time in MS we'll wait before recursively calling the load page function to see if we have an
 * available slot to send a request.
 *
 * @type {number}
 */
const TIME_BETWEEN_PAGE_REQUESTS_IN_MS = 1000;

export default Service.extend(ShowDeclineOtherApplicationsModalMixin, {
  /**
   * Register services
   */
  session: service(),
  store: service(),
  applicationCommandService : service('commands/application-commands'),
  applicationStateManager: service('state-managers/application'),
  assignmentCommandService : service('commands/assignment-commands'),
  threadCommandService: service('commands/thread-commands'),
  threadManager: service('managers/threadManager'),
  errorTracker: service('error-tracker'),
  state: service('states/routes/posting'),

  currentUser: readOnly('session.currentUser'),

  /**
   * Approve and offer and update state
   *
   * @param {String} applicationId
   */
  approveOffer: task(function *(applicationId) {

    try {
      yield this.applicationCommandService.approveOffer(applicationId);
      const response = yield this.applicationCommandService.loadApplication(applicationId);
      this.store.pushPayload(response);
      const applicationUpdated = this.store.peekRecord('application', applicationId);

      if (!this.state.isSearchActivated && applicationUpdated.status === APPLICATION.STATUS.ACTIVE) {
        //we're not in search AND we're at active state.  Seller could still be in PENDING if they have not accepting legal with
        //this company.
        const applications = this.state.assignmentDetailsApplicationList.applications;
        applications.removeObject(applicationUpdated);

        const assignmentDetailsAppList = this.state.assignmentDetailsApplicationList;
        assignmentDetailsAppList.set('hiredCount', assignmentDetailsAppList.hiredCount + 1);
        assignmentDetailsAppList.set('pendingCount', assignmentDetailsAppList.pendingCount - 1);
      }
      this.applicationStateManager.addToast('Offer approved');
    } catch(e) {
      this.errorTracker.error('approveOffer - ' + applicationId, this);
    }
  }),

  /**
   * Clear the current search results
   */
  clearSearchResults() {
    this.set('state.applicationSearchResults', A());
  },

  /**
   * Clear all selected applications in our state.
   */
  clearSelectionState() {
    const updatedSelectedMap = Object.keys(this.state.totalApplicationsSelectedMap).reduce((accum, key) => {
      accum[key] = false;

      return accum;
    }, {});

    this.setProperties({
      'state.totalApplicationsSelected': [],
      'state.isSelectAllChecked': false,
      'state.totalApplicationsSelectedMap': updatedSelectedMap,
    });
  },

  /**
   * Decline the given application ids and update our state.
   *
   * @param {Array<String>} applicationIds
   * @param {String} message
   */
  declineApplications(applicationIds, message) {
    return this.applicationCommandService.declineApplications(
      applicationIds,
      message).then(() => {
        let applicationsDeclinedCount = applicationIds.length,
          applications = this.get('state.assignmentDetailsApplicationList.applications');

        applicationIds.forEach(applicationId => {
          let application = this.get('store').peekRecord('application', applicationId);
          application.set('status', APPLICATION.STATUS.DECLINED);

          let appToRemove = applications.find(application => application.get('id') === applicationId);
          applications.removeObject(appToRemove);
        });

        let assignmentDetailsAppList = this.get('state.assignmentDetailsApplicationList');
        assignmentDetailsAppList.set('declinedCount', assignmentDetailsAppList.get('declinedCount') + applicationsDeclinedCount);
        assignmentDetailsAppList.set('pendingCount', assignmentDetailsAppList.get('pendingCount') - applicationsDeclinedCount);

        this.clearSelectionState();

        this.get('applicationStateManager').addToast(
          applicationsDeclinedCount === 1 ?
            'Proposal declined' :
            'Proposals declined');
      }).catch(e => this.get('errorTracker').error('declineApplications - ' + applicationIds.toString(), this));
  },

  /**
  * Unecline the given application id and update our state.
  *
  * @param {String} applicationId
  */
  undeclineApplication(applicationId) {
    return this.applicationCommandService.undeclineApplication(applicationId).then(() => {
      this.state.setProperties({
        currentPagination: null,
        assignmentDetailsApplicationList: null,
        currentCacheKey: v1(),
      });

      return this.loadApplications(
        this.state.assignment.id,
        this.state.currentApplicationFilterStatus,
        1,
        POSTING_APPLICATIONS_PER_PAGE,
        this.state.sortSelectedValue,
        this.state.networkFilterSelectedValue,
        this.state.secondarySort,
        true
      );
    }).catch((e) => {
      this.errorTracker.error('undeclineApplication - ' + applicationId, this, e)
      throw e;
    });
  },

  /**
   * Decrement one page of results if possible.
   *
   * @param {String} sort             of type POSTING_APPLICATIONS_SORT_API_ENUMS.*
   * @param {Boolean} isOnlyShowFavorites    true if we want to filter only by favorites
   * @param {String} secondarySort    of type ICON_SORTS.*
   * @return {*|Ember.RSVP.Promise}
   */
  decrementPage(sort, isOnlyShowFavorites, secondarySort) {
    let pagination = this.get('state.currentPagination'),
      newPageNumber = pagination.get('currentPage') > 1 ? pagination.get('currentPage') - 1 : 1,
      resultsPerPage = pagination.get('perPage');

    return this.loadApplications(
      this.get('state.assignment.id'),
      this.get('state.currentApplicationFilterStatus'),
      newPageNumber,
      resultsPerPage,
      sort,
      isOnlyShowFavorites,
      secondarySort
    );
  },

  /**
   * Increment one page of results if possible.
   *
   * @param {String} sort             of type POSTING_APPLICATIONS_SORT_API_ENUMS.*
   * @param {Boolean} isOnlyShowFavorites    true if we want to filter only by favorites
   * @param {String} secondarySort    of type ICON_SORTS.*
   * @return {*|Ember.RSVP.Promise}
   */
  incrementPage(sort, isOnlyShowFavorites, secondarySort) {
    let pagination = this.get('state.currentPagination'),
      newPageNumber = pagination.get('currentPage') + 1,
      resultsPerPage = pagination.get('perPage');

    return this.loadApplications(
      this.get('state.assignment.id'),
      this.get('state.currentApplicationFilterStatus'),
      newPageNumber,
      resultsPerPage,
      sort,
      isOnlyShowFavorites,
      secondarySort
    );
  },

  /**
   * Load a specific page for the current sort, filter state.
   *
   * @param {Number} page
   * @return {Ember.RSVP.Promise}
   */
  loadSpecificPage(page) {
    let pagination = this.get('state.currentPagination'),
      resultsPerPage = pagination.get('perPage');

    return this.loadApplications(
      this.get('state.assignment.id'),
      this.get('state.currentApplicationFilterStatus'),
      page,
      resultsPerPage,
      this.get('state.sortSelectedValue'),
      this.get('state.networkFilterSelectedValue'),
      this.get('state.secondarySort')
    );
  },

  /**
   * Load the list of Applications for the view.
   *
   * @param {String}  assignmentId        the id of the assignment to fetch apps for.
   * @param {String}  status              of type APPLICATION.SIMPLE_STATUS.*
   * @param {Number=} page            The current page number we'd like to fetch
   * @param {Number=} perPageCount    The count of how many items we want per page.
   * @param {String=} sort            of type POSTING_APPLICATIONS_SORT_API_ENUMS.*
   * @param {Boolean=} isOnlyShowFavorites    true if we want to filter only by favorites
   * @param {String=} secondarySort   of type ICON_SORTS.*
   * @param {Boolean=} skipCache      Set to true to always skip the client side cache.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadApplications(
    assignmentId,
    status,
    page = 1,
    perPageCount = POSTING_APPLICATIONS_PER_PAGE,
    sort = POSTING_APPLICATIONS_SORT_API_ENUMS.LAST_ACTIVITY,
    isOnlyShowFavorites = false,
    secondarySort = ICON_SORTS.SORT_DESCENDING,
    skipCache = false
  ) {

    const { pageToResultsMap, currentCacheKey } = this.state;

    //check our cache
    if (!skipCache && pageToResultsMap[page]) {
      this.set('state.assignmentDetailsApplicationList', pageToResultsMap[page]);
      return Promise.resolve(pageToResultsMap[page]);
    }

    this.state.setProperties({
      isLoadingResult: true,
      currentApplicationFilterStatus: status,
      secondarySort
    });

    //not in cache, let's call the DB.
    return this.applicationCommandService.loadApplications(
      assignmentId,
      status,
      sort,
      isOnlyShowFavorites,
      secondarySort,
      page,
      perPageCount,
      currentCacheKey,
      `pitch.image,buyer,seller.location,seller.last-location,seller.projectdigests:sort(classification-match-preferred):limit(1):assignment_id(${assignmentId}),thread.messages,assignment,` +
      'seller.projectdigests.company,seller.projectdigests.image,seller.projectdigests.services,seller.projectdigests.oembed,' +
      'seller.projectdigests.review.reviewer,seller.projectdigests.classifications,seller.services,seller.experiences,assignment-question-responses',
    ).then((assignmentDetailsApplicationList) => {
        const {
          currentCacheKey,
          pageToResultsMap,
        } = this.state;

        if (currentCacheKey !== assignmentDetailsApplicationList.cacheKey) {
          return;
        }

        this.setProperties({
          'state.isLoadingResult': false,
          'state.pageToResultsMap': {
            ...pageToResultsMap,
            [page]: assignmentDetailsApplicationList,
          },
        });

        //update multi-select state with these current applications
        this._setMultiSelectState(assignmentDetailsApplicationList.applications);

        //update state.
        this.set('state.assignmentDetailsApplicationList', assignmentDetailsApplicationList);

        //if this pagination is later than the current one, we'll save it as the latest.
        const requestPagination = assignmentDetailsApplicationList.pagination;
        const currentPagination = this.state.currentPagination ?? requestPagination;

        if (!this.state.currentPagination) {
          //if we don't have a latest pagination, we should always set the one from the request.
          this.set('state.currentPagination', requestPagination);
        }

        if (requestPagination.currentPage >= currentPagination.currentPage) {
          this.set('state.currentPagination', requestPagination);
        }

        //successfully got our first page, no let's start pre-fetching.
        this._loadAllPages();
      })
      .catch(e => {
        this.errorTracker.error('loadApplications - ' + arguments.toString() + e.toString(), this);
        this.set('state.isLoadingResult', false);
      });
  },

  /**
   * Load the given assignment id from cache or server if it isn't there.
   *
   * @param {String} assignmentId
   * @param {Boolean?} includeCompanyTeams
   * @return {Promise}
   */
  loadAssignmentForId(assignmentId) {
    this.set('state.isLoadingAssignment', true);

    const includes = this.currentUser.isSeller
      ? POSTING__ASSIGNMENT_SELLER_INCLUDES
      : POSTING__ASSIGNMENT_BUYER_INCLUDES;

    return this.get('assignmentCommandService').loadAssignment(assignmentId, includes)
      .then((assignment) => {
          this.set('state.assignment', assignment);
          return assignment;
      })
      .catch(e => this.get('errorTracker').error(`loadAssignmentForId - ${assignmentId} - ${JSON.stringify(e)}`, this))
      .finally(() => this.set('state.isLoadingAssignment', false));
  },

  /**
   * Reset the state to the initial setup.
   *
   * @param {Boolean=} opt_isPreserveAssignmentState    set to true if you want to preserve the assignment
   *                                                    state and just reset the applicationList part.
   * @param {Boolean=} opt_isPreserveApplicationSortFilterStatus  set to true to preserve the application
   *                                                              sorts, filter, and status.
   * @param {Boolean=} opt_isResetFirstQuery            Set this to true to reset the isFirstQuery flag
   */
  resetStateManager(opt_isPreserveAssignmentState, opt_isPreserveApplicationSortFilterStatus, opt_isResetFirstQuery) {
    let currentAssignment   = this.get('state.assignment'),
      applicationStatus     = this.get('state.currentApplicationFilterStatus'),
      sortSelected          = this.get('state.sortSelectedValue'),
      secondarySort         = this.get('state.secondarySort'),
      isFilterFavorites     = this.get('state.isFilterFavorites');

    this.toggleIsStateResetting(true);
    this.state.initializeDefaults();

    if (opt_isPreserveAssignmentState) {
      this.set('state.assignment', currentAssignment);
    }

    if (opt_isPreserveApplicationSortFilterStatus) {
      this.set('state.currentApplicationFilterStatus', applicationStatus);
      this.set('state.isFilterFavorites', isFilterFavorites);
      this.set('state.secondarySort', secondarySort);
      this.set('state.sortSelectedValue', sortSelected);
    }

    if (opt_isResetFirstQuery) {
      this.set('state.isFirstQuery', true);
    }

    this.toggleIsStateResetting(false);
  },

  /**
   * Given a string text value, search the database for the applications containing that.
   *
   * @param {String} textValue
   * @return {Ember.RSVP.Promise}
   */
  searchApplicationsForText(textValue) {
    this.set('state.isLoadingSearchResult', true);
    this.set('state.currentCacheKeySearch', v1());

    return this.get('applicationCommandService').searchApplications(
      textValue,
      this.get('state.assignment.id'),
      this.get('state.currentCacheKeySearch')).then((applications) => {
        let currentStateCacheKey = this.get('state.currentCacheKeySearch');
        if (currentStateCacheKey !== applications.get('meta.cache_key')) {
          return;
        }

        this.set('state.isLoadingSearchResult', false);
        this.set('state.applicationSearchResults', applications);

        //update multi-select state with these current applications
        this._setMultiSelectState(applications);
      });
  },

  /**
   *
   */
  searchToggleIsActivated() {
    if (this.get('state.isSearchActivated')) {
      //search has been activated, let's move these results into the cache and reset our state
      if (this.get('state.assignmentDetailsApplicationList') && !this.get('state.assignmentDetailsApplicationListWhileSearchingCache')) {
        //we have results in our dashboard list, but none in our cache, so let's move these over.
        this.swapAssignmentApplicationListToCache();
      }
    } else {
      //search has been deactivated, let's reset our state.

      this.set('state.isLoadingSearchResult', false);
      this.set('state.currentCacheKeySearch', v1());

      this.clearSearchResults();
      this.swapCacheToAssignmentApplicationList();
      this.set('state.isLoadingAllPages', false);
    }
  },

  /**
   * The select all checkbox has been changed by the user.
   */
  selectAllChanged() {
    if (this.get('state.isSelectAllChecked')) {
      //The user selected the 'check all' box, so we need to load all the pages and select all the items.
      let pagination = this.get('state.currentPagination');
      if (pagination.get('currentPage') < pagination.get('totalPages')) {
        this._loadAllPages(true);
      }

      this._markAllApplicationsChecked(true);
    } else {
      this._markAllApplicationsChecked(false);
    }
  },

  /**
   * Send an offer and update state.
   *
   * @param {String} applicationId
   * @param {Number} price
   */
  sendOffer: task(function *(applicationId, price) {
    try {
      yield this.applicationCommandService.makeOffer(applicationId, price);
      const response = yield this.applicationCommandService.loadApplication(applicationId);
      this.store.pushPayload(response);
      const application = this.store.peekRecord('application', applicationId);

      this.applicationStateManager.addToast('Offer sent');

      if (this.currentUser.isBuyer && application.assignment.type === ASSIGNMENT.TYPES.STANDARD) {
        this.showDeclineOtherApplicationsModal(application.assignment, application.seller).then(() => {
          this.applicationStateManager.addToast('Confirmed');
        }).catch(() => {
          //gobble
        });
      }
    } catch (e) {
      handleErrorResponseWithModal(
        this,
        e,
        null,
        `sendOffer - ${applicationId}`,
        'There was an error sending your offer.'
      );
    }
  }),

  /**
   * Send messages and update our state for the given thread ids.
   *
   * @param {String} message
   * @param {Array<String>} threadIds
   */
  sendMessages(message, threadIds) {
    return this.get('threadCommandService').sendMessageToThreadIds(message, threadIds).then(() => {
      this.clearSelectionState();

      this.get('applicationStateManager').addToast('Message sent.');
    }).catch(e => this.get('errorTracker').error('sendMessages - ' + threadIds.toString(), this));
  },

  /**
   * Set the isFirstQuery flag to false
   */
  setNotFirstQuery() {
    this.set('state.isFirstQuery', false);
  },

  /**
   * Swap our previous results cache with the active results list
   */
  swapCacheToAssignmentApplicationList() {
    let assignmentDetailsList = this.get('state.assignmentDetailsApplicationListWhileSearchingCache');
    this.set('state.assignmentDetailsApplicationList', assignmentDetailsList);
    this.set('state.assignmentDetailsApplicationListWhileSearchingCache', null);

    if (assignmentDetailsList && assignmentDetailsList.get('applications.length')) {
      this._setMultiSelectState(A(), true);
      let pagesOfResults = this.get('state.pageToResultsMap');
      Object.keys(pagesOfResults).forEach((pageNumber) => {
        this._setMultiSelectState(pagesOfResults.get(pageNumber).get('applications'));
      });
    }
  },

  /**
   * Put active results list into the cache.
   */
  swapAssignmentApplicationListToCache() {
    this.set('state.assignmentDetailsApplicationListWhileSearchingCache', this.get('state.assignmentDetailsApplicationList'));
    this.set('state.assignmentDetailsApplicationList', null);

    this._setMultiSelectState(A(), true);
  },

  /**
   * Toggle the application selected in the multi-select view and update appropriate state.
   *
   * @param {String} applicationId
   * @param {Boolean} isSelected
   */
  toggleApplicationSelected(applicationId, isSelected) {
    let selectedApps = this.get('state.totalApplicationsSelected');

    if (isSelected) {
      if (!selectedApps.includes(applicationId)) {
        selectedApps.pushObject(applicationId);
      }
      this.set('state.isShowSelectAllOptions', true);

      let currentPagination = this.get('state.currentPagination');
      if (selectedApps.get('length') === currentPagination.get('total')) {
        this.set('state.isSelectAllChecked', true);
      }
    } else {
      selectedApps.removeObject(applicationId);
      if (!selectedApps.get('length')) {
        this.set('state.isShowSelectAllOptions', false);
      }
    }
  },

  /**
   * Toggle the stateIsResetting boolean
   *
   * @param {Boolean} isResetting
   */
  toggleIsStateResetting(isResetting) {
    this.set('state.isResettingState', isResetting);
  },

  /**
   * Update the count and the previous property counts for the given prop names
   *
   * @param {String} countPropertyName          The active property name e.g. 'hiredCount'
   * @param {String} countPreviousPropertyName  The previous count for the property name e.g. 'previousHiredCount'
   */
  updateCount(countPropertyName, countPreviousPropertyName) {
    let previousCount = this.get(`state.${countPreviousPropertyName}`),
      currentCount    = this.get(`state.${countPropertyName}`);

    if (currentCount) {
      this.set(`state.${countPreviousPropertyName}`, currentCount);
      return currentCount;
    }

    return previousCount;
  },

  /**
   * Load all the pages for the current pagination.  This will load up to NUMBER_OF_PAGES_TO_LOAD_IN_PARALLEL
   * at any given time.
   *
   * @param {Boolean=} opt_isSelectAll
   * @private
   */
  _loadAllPages(opt_isSelectAll) {
    if (this.get('state.isLoadingAllPages')) {
      return;
    }

    let pageToResultsMap = this.get('state.pageToResultsMap'),
      pagination = this.get('state.currentPagination'),
      totalPages = pagination.get('totalPages');

    if (totalPages === 1 && pagination.get('currentPage') === 1) {
      //we have exactly 1 page, so no need to try to load other pages.
      return;
    }

    this.set('state.isLoadingAllPages', true);

    for (let i = 1; i <= totalPages; i++) {
      if (!pageToResultsMap[i]) {
        this._loadPageAsync(i, opt_isSelectAll);
      }
    }
  },

  /**
   * Load a page in async.  This will recursively call itself if we don't have a slot with the max number
   * of pages to load in parallel.
   *
   * @param {Number} pageNumber
   * @param {Boolean=} opt_isSelectAll
   * @private
   */
  _loadPageAsync(pageNumber, opt_isSelectAll) {
    if (!this.get('state.isLoadingAllPages')) {
      return;
    }

    let currentOutstandingRequests = this.get('state.currentOutstandingPagesCount'),
      currentPagination = this.get('state.currentPagination');
    if (currentOutstandingRequests >= NUMBER_OF_PAGES_TO_LOAD_IN_PARALLEL) {
      //we have to wait, let's kick this off later.
      later(this, () => this._loadPageAsync(pageNumber), TIME_BETWEEN_PAGE_REQUESTS_IN_MS);
      return;
    }

    if (!currentPagination) {
      //if we don't have a pagination, this could be due to an async request coming back after this has been
      //destroyed, so we can stop here.
      return;
    }

    this.set('state.currentOutstandingPagesCount', currentOutstandingRequests + 1);

    this.loadSpecificPage(pageNumber)
      .then(() => {
        if (opt_isSelectAll) {
          this._markAllApplicationsChecked(true);
        }
      })
      .catch(e => this.get('errorTracker').error('_loadPageAsync - ' + ' page ' + pageNumber + e.toString(), this))
      .finally(() => {
      let outstandingRequests = this.get('state.currentOutstandingPagesCount');

      this.set('state.currentOutstandingPagesCount', --outstandingRequests);
      if (outstandingRequests <= 0 && currentPagination.get('total') === this.get('state.applicationListItems.length')) {
          this.set('state.isLoadingAllPages', false);

          if (this.get('state.isSelectAllChecked')) {
            this._markAllApplicationsChecked(true);
          }
      }
    });
  },

  /**
   * Mark all of our current applications as checked on the client.
   *
   * @param {Boolean} isChecked   true to mark all checked or false otherwise.
   * @private
   */
  _markAllApplicationsChecked(isChecked) {
    const selectedMapKeys = Object.keys(this.state.totalApplicationsSelectedMap);

    const updatedSelectedApplications = selectedMapKeys.reduce((accum, applicationId) => {
      if (isChecked) {
        accum.push(applicationId);
      }

      return accum;
    }, []);

    const updatedChecked = selectedMapKeys.reduce((accum, key) => {
      accum[key] = isChecked;

      return accum;
    }, {});

    this.setProperties({
      'state.totalApplicationsSelectedMap': updatedChecked,
      'state.totalApplicationsSelected': updatedSelectedApplications,
    });
  },

  /**
   * Update our multi-select state.  Initializes state of our object that tracks selection of all
   * applications on the page.
   *
   * @param {Ember.MutableArray<Application>} applications
   * @param {Boolean=} opt_isClearState       True if you want to clear all existing multi-select state and re-initialize.
   * @private
   */
  _setMultiSelectState(applications, opt_isClearState) {
    const applicationMultiSelectMap = opt_isClearState
      ? {}
      : this.state.totalApplicationsSelectedMap;

    if (opt_isClearState) {
      this.clearSelectionState();
    }

    const updatedChecked = applications.reduce((accum, application) => {
      if (typeof applicationMultiSelectMap[application.id] === 'undefined') {
        accum[application.id] = false;
      }

      return accum;
    }, applicationMultiSelectMap);

    this.set('state.totalApplicationsSelectedMap', updatedChecked);
  }
});
